import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, Phone } from "lucide-react";
import { useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Framer-motion variants — line-by-line masked reveal.
const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] } },
};

// Reveal a single line clipped inside overflow-hidden — this is the signature
// on-load "curtain lift" moment.
const RevealLine = ({ children, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className={`block ${className}`}>{children}</motion.span>
  </span>
);

export const Hero = () => {
  const { lang, hasImage } = useApp();
  const t = useTranslation(lang);
  const heroUploaded = hasImage("hero");

  // Mouse-follow parallax — spring-smoothed for silky feel.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });
  const bgX = useTransform(sx, (v) => v * -22);
  const bgY = useTransform(sy, (v) => v * -22);
  const titleX = useTransform(sx, (v) => v * 12);
  const titleY = useTransform(sy, (v) => v * 8);

  useEffect(() => {
    const onMove = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[100svh] h-screen w-full overflow-hidden">
      {/* Background media with parallax */}
      <motion.div style={{ x: bgX, y: bgY, scale: 1.08 }} className="absolute inset-0">
        {heroUploaded ? (
          <img
            data-testid="hero-image"
            src={`${API}/images/hero`}
            alt="Restaurante Q5"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1628610688436-e635552020fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          >
            <source src="https://videos.pexels.com/video-files/4253691/4253691-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/3296241/3296241-uhd_2560_1440_25fps.mp4" type="video/mp4" />
          </video>
        )}
      </motion.div>

      <div className="absolute inset-0 hero-video-overlay" />
      <div className="grain-overlay" />

      {/* Editorial side markers */}
      <div className="pointer-events-none absolute inset-y-0 left-6 hidden lg:flex flex-col items-start justify-between py-10 z-10">
        <motion.span initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8 }} className="text-[10px] uppercase tracking-[0.4em] text-white/70 [writing-mode:vertical-rl] rotate-180">Est. 2018 · Moralzarzal</motion.span>
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }} className="text-[10px] uppercase tracking-[0.4em] text-gold/80">Nº 001 / Q5</motion.span>
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-6 hidden lg:flex flex-col items-end justify-between py-10 z-10">
        <motion.span initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8 }} className="text-[10px] uppercase tracking-[0.4em] text-white/70">40.6789° N · 4.0311° W</motion.span>
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }} className="text-[10px] uppercase tracking-[0.4em] text-gold/80 [writing-mode:vertical-rl]">餐廳 · Cocina Asiática</motion.span>
      </div>

      {/* Kinetic centred stack */}
      <motion.div
        style={{ x: titleX, y: titleY }}
        variants={lineParent}
        initial="hidden"
        animate="visible"
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6"
      >
        <RevealLine className="overline">
          {t.hero.overline}
        </RevealLine>

        <h1 className="font-serif-q5 text-white mt-6 leading-[0.9]">
          <RevealLine className="text-4xl md:text-6xl font-light tracking-wide opacity-90">
            {t.hero.title1}
          </RevealLine>
          <RevealLine className="text-7xl md:text-[9rem] font-medium tracking-tight text-gold italic mt-1">
            {t.hero.title2}
          </RevealLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="my-8 flex items-center gap-4 origin-center"
        >
          <span className="h-px w-16 bg-gold/70" />
          <span className="text-gold text-xs md:text-sm tracking-[0.35em]">★ 4,6 / 5 · GOOGLE</span>
          <span className="h-px w-16 bg-gold/70" />
        </motion.div>

        <RevealLine className="max-w-2xl text-neutral-200 text-base md:text-lg leading-relaxed">
          {t.hero.subtitle}
        </RevealLine>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            data-testid="hero-call-btn"
            href={`tel:${PHONES[0].tel}`}
            className="btn-primary-q5 group"
          >
            <Phone size={14} className="group-hover:animate-pulse" /> {t.hero.cta1}
          </a>
          <button
            data-testid="hero-menu-btn"
            onClick={() => scrollTo("menu")}
            className="btn-secondary-q5"
          >
            {t.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-down"
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-neutral-300 hover:text-gold flex flex-col items-center gap-2 group"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <ChevronDown size={20} className="animate-bounce group-hover:text-gold" />
      </motion.button>
    </section>
  );
};
